import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSupport } from "../../Support/context/SupportContext";
import { useAuth } from "../../context/AuthContext";
import { CurrentSupportSubscription } from "../components/support/CurrentSupportSubscription";
import { HandsOnSupportInfo } from "../components/support/HandsOnSupportInfo";
import { SupportPlanCard } from "../components/support/SupportPlanCard";
import { SupportFAQ } from "../components/support/SupportFAQ";
import { CancelSupportModal } from "../components/support/CancelSupportModal";
import { BillingToggle } from "../components/support/BillingToggle";
import { SupportCheckoutModal } from "../components/support/SupportCheckoutModal";
import { useGeoLocation } from "../hooks/useGeoLocation";

export function SupportTab({ paymentMethods = [] }) {
    const { t } = useTranslation();
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showCheckoutModal, setShowCheckoutModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [isYearly, setIsYearly] = useState(false);
    const { isLatam } = useGeoLocation();
    const { user } = useAuth();
    const {
        supportPlans,
        supportSubscription,
        isLoadingPlans,
        isCancelingSubscription,
        isPausingSubscription,
        isResumingSubscription,
        getSupportSubscription,
        cancelSupportSubscription,
        pauseSupportSubscription,
        resumeSupportSubscription,
    } = useSupport();

    useEffect(() => {
        getSupportSubscription();
    }, [getSupportSubscription]);

    const handlePurchasePlan = (tier) => {
        const plan = supportPlans.find((p) => p.tier === tier);
        setSelectedPlan(plan);
        setShowCheckoutModal(true);
    };

    const subscription = supportSubscription?.subscription;
    const planName = subscription?.tier?.replace(/_/g, " ").toLowerCase();

    return (
        <div className="space-y-8">
            <CurrentSupportSubscription
                subscription={subscription}
                onManage={() => setShowCancelModal(true)}
                onResume={resumeSupportSubscription}
                isResuming={isResumingSubscription}
            />
            <HandsOnSupportInfo />
            <SupportPlansGrid
                supportPlans={supportPlans}
                subscription={subscription}
                isLoadingPlans={isLoadingPlans}
                onPurchase={handlePurchasePlan}
                isYearly={isYearly}
                onToggleBilling={setIsYearly}
                isLatam={isLatam}
                t={t}
            />
            <SupportFAQ />

            <CancelSupportModal
                isOpen={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onCancel={cancelSupportSubscription}
                onPause={pauseSupportSubscription}
                planName={planName}
                isCanceling={isCancelingSubscription}
                isPausing={isPausingSubscription}
            />

            <SupportCheckoutModal
                isOpen={showCheckoutModal}
                onClose={() => { setShowCheckoutModal(false); setSelectedPlan(null); }}
                selectedPlan={selectedPlan}
                isYearly={isYearly}
                isLatam={isLatam}
                userId={user?.id}
                paymentMethods={paymentMethods || []}
            />
        </div>
    );
}

function SupportPlansGrid({ supportPlans, subscription, isLoadingPlans, onPurchase, isYearly, onToggleBilling, isLatam, t }) {
    if (isLoadingPlans) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div>
            <h3 className="text-lg font-semibold mb-4 text-center">
                {subscription ? t("billing.supportPlans.upgradeYourPlan") : t("billing.supportPlans.choosePlan")}
            </h3>
            {isLatam && (
                <p className="text-center text-green-600 text-sm mb-2">
                    🌎 {t("billing.supportPlans.latamPricing")}
                </p>
            )}
            <BillingToggle isYearly={isYearly} onChange={onToggleBilling} />
            <div className="grid md:grid-cols-3 gap-6">
                {supportPlans.map((plan) => (
                    <SupportPlanCard
                        key={plan.tier}
                        plan={plan}
                        isCurrentPlan={subscription?.tier === plan.tier}
                        hasSubscription={!!subscription}
                        onPurchase={onPurchase}
                        isYearly={isYearly}
                        isLatam={isLatam}
                    />
                ))}
            </div>
        </div>
    );
}
